<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CurrencyInput from 'dashboard/components-next/input/CurrencyInput.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import SavedPixKeyCard from './SavedPixKeyCard.vue';
import {
  buildPixMessagePayload,
  validPixAmount,
  validPixCode,
} from './pixPayment';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  isLoading: { type: Boolean, default: false },
});
const emit = defineEmits(['send']);
const { t } = useI18n();
const dialogRef = ref(null);
const keyCardRef = ref(null);
const session = ref(0);
const amount = ref('');
const description = ref('');
const payment = ref(null);
const valid = computed(
  () =>
    payment.value &&
    validPixAmount(payment.value.amount) &&
    validPixCode(payment.value.pix_code)
);
const summary = computed(() =>
  payment.value
    ? new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(Number(payment.value.amount))
    : ''
);
const busy = computed(() => props.isLoading || !!keyCardRef.value?.generating);
const canConfirm = computed(() =>
  valid.value
    ? payment.value.delivery_mode === 'native'
    : !!keyCardRef.value?.canGenerate
);
const reset = () => {
  session.value += 1;
  amount.value = '';
  description.value = '';
  payment.value = null;
};
const open = () => {
  reset();
  dialogRef.value?.open();
};
const close = () => dialogRef.value?.close();
const confirm = async () => {
  if (busy.value || !canConfirm.value) return;
  if (!valid.value) {
    await keyCardRef.value?.generate();
    return;
  }
  emit(
    'send',
    buildPixMessagePayload(payment.value, t('PIX_KEYS.PAYMENT.DEFAULT_BODY'))
  );
};
defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    solid
    overflow-y-auto
    sticky-header-footer
    :close-on-click-outside="false"
    :title="t('PIX_KEYS.PAYMENT.MODAL_TITLE')"
    :description="t('PIX_KEYS.PAYMENT.HELP')"
    :confirm-button-label="
      valid ? t('PIX_KEYS.PAYMENT.SEND') : t('PIX_KEYS.PAYMENT.GENERATE')
    "
    :disable-confirm-button="!canConfirm"
    :is-loading="busy"
    @confirm="confirm"
    @close="reset"
  >
    <div class="grid gap-4">
      <CurrencyInput
        v-model="amount"
        :label="t('CONVERSATION.INTERACTIVE.PIX.AMOUNT')"
        :disabled="busy"
      />
      <NextInput
        v-model="description"
        :label="t('CONVERSATION.INTERACTIVE.PIX.DESCRIPTION')"
        :disabled="busy"
        maxlength="120"
      />
      <SavedPixKeyCard
        ref="keyCardRef"
        :key="session"
        compact
        :conversation-id="conversationId"
        :amount="amount"
        :description="description"
        :disabled="isLoading"
        @generated="payment = $event"
        @invalidated="payment = null"
      />
      <div
        v-if="valid"
        class="grid gap-2 rounded-xl border border-n-weak bg-n-solid-2 p-4"
        role="status"
      >
        <p class="m-0 text-lg font-semibold text-n-slate-12">{{ summary }}</p>
        <p class="m-0 text-sm text-n-slate-12">{{ payment.merchant_name }}</p>
        <p class="m-0 text-xs text-n-slate-11">
          {{ t('PIX_KEYS.PAYMENT.GENERATED') }}
        </p>
        <p class="m-0 text-xs text-n-slate-11" data-test="pix-delivery-mode">
          {{
            payment.delivery_mode !== 'native'
              ? t('PIX_KEYS.PAYMENT.UNSUPPORTED_NOTICE')
              : t('PIX_KEYS.PAYMENT.NATIVE_NOTICE')
          }}
        </p>
      </div>
      <p class="m-0 text-xs text-n-slate-11">
        {{ t('PIX_KEYS.PAYMENT.NOTICE') }}
      </p>
    </div>
  </Dialog>
</template>
